import { useEffect, useState } from "react";
import type { CSSProperties } from "react";

export interface FloatingPanelPosition {
  /** Top-Koordinate in Viewport-Pixeln (für `position: fixed`). */
  top: number;
  /** Left-Koordinate in Viewport-Pixeln. */
  left: number;
  /** Empfohlene Breite – entspricht der Triggerbreite. */
  width: number;
  /** Maximale verfügbare Höhe nach unten/oben (für `max-height`). */
  maxHeight: number;
  /** True, wenn das Panel oberhalb des Triggers gerendert werden soll. */
  flipped: boolean;
  /** z. B. `translateY(-100%)`, wenn das Panel am unteren Rand andockt. */
  transform?: string;
}

interface UseFloatingPanelOptions {
  /** Vertikaler Abstand zwischen Trigger und Panel in Pixeln (Default 4). */
  gap?: number;
  /**
   * Fallback-Mindesthöhe für die Flip-Entscheidung, wenn keine Schätzung
   * übergeben wird (Default 160).
   */
  minBelow?: number;
  /** Hard-Cap für die maximale Höhe (Default 288 = `max-h-72`). */
  maxHeightCap?: number;
  /**
   * Geschätzte Inhaltshöhe des Panels (z. B. aus Anzahl Optionen).
   * Steuert Flip und verhindert große Lücken bei kurzen Listen oben.
   */
  contentHeightEstimate?: number;
}

/** CSS für ein per Portal gerendertes Dropdown (fixed + optional Flip). */
export function floatingPanelStyle(pos: FloatingPanelPosition): CSSProperties {
  return {
    position: "fixed",
    top: pos.top,
    left: pos.left,
    width: pos.width,
    maxHeight: pos.maxHeight,
    ...(pos.transform ? { transform: pos.transform } : {}),
  };
}

/** Grobe Höhenschätzung für Autocomplete-Listen. */
export function estimateAutocompletePanelHeight(options: {
  optionCount: number;
  loading?: boolean;
  empty?: boolean;
  hasDescription?: boolean;
  extraRows?: number;
  maxHeightCap?: number;
}): number {
  const {
    optionCount,
    loading = false,
    empty = false,
    hasDescription = false,
    extraRows = 0,
    maxHeightCap = 320,
  } = options;
  if (loading) return 40;
  if (empty) return 64;
  const rowHeight = hasDescription ? 52 : 36;
  const rows = Math.max(optionCount, 0) + extraRows;
  return Math.min(rows * rowHeight + 8, maxHeightCap);
}

/**
 * Berechnet eine viewport-fixierte Position für ein Dropdown-Panel relativ
 * zu einem Trigger-Element.
 *
 * - Liefert `position: fixed`-kompatible Koordinaten (linker oberer Punkt).
 * - Klemmt die Höhe auf den verfügbaren Platz im Viewport.
 * - "Flipped" (oberhalb des Triggers), wenn der geschätzte Inhalt unten
 *   nicht passt – nicht schon bei kurzen Listen mit festem minBelow.
 * - Im Flip-Modus dockt die Unterkante des Panels am oberen Trigger-Rand an
 *   (`translateY(-100%)`), statt die volle maxHeight nach oben zu reservieren.
 */
export function useFloatingPanel(
  triggerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  options: UseFloatingPanelOptions = {},
): FloatingPanelPosition | null {
  const {
    gap = 4,
    minBelow = 160,
    maxHeightCap = 288,
    contentHeightEstimate,
  } = options;
  const [pos, setPos] = useState<FloatingPanelPosition | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }

    function update() {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const spaceBelow = Math.max(0, viewportH - rect.bottom - gap - 8);
      const spaceAbove = Math.max(0, rect.top - gap - 8);

      const neededHeight = Math.min(
        Math.max(contentHeightEstimate ?? minBelow, 40),
        maxHeightCap,
      );

      const flipped = spaceBelow < neededHeight && spaceAbove > spaceBelow;

      const available = Math.min(flipped ? spaceAbove : spaceBelow, maxHeightCap);

      if (flipped) {
        setPos({
          top: rect.top - gap,
          left: rect.left,
          width: rect.width,
          maxHeight: available,
          flipped: true,
          transform: "translateY(-100%)",
        });
        return;
      }

      setPos({
        top: rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight: available,
        flipped: false,
      });
    }

    update();
    const raf = window.requestAnimationFrame(update);

    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, triggerRef, gap, minBelow, maxHeightCap, contentHeightEstimate]);

  return pos;
}
